"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Zap, ShieldCheck, Award, Compass, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
interface BenefitItem {
  id: string;
  title: string;
  subtitle: string;
  description: React.ReactNode;
  highlight: string;
  iconName: string;
}
const benefits: BenefitItem[] = [{
  id: "early-stage-research",
  title: "Early-stage research",
  subtitle: "Discovery & evaluation",
  description: <>
        Buyers now ask ChatGPT, Google AI Overviews, Perplexity and Bing Copilot to explore solutions and map out options. Being clearly understood and referenced during this initial fact-finding phase shapes shortlist consideration well before a prospect clicks through to any website. Building sound{" "}
        <Link to="/TechnicalSeoPage" className="text-primary font-medium underline underline-offset-2 hover:text-accent transition-colors">
          technical SEO foundations
        </Link>{" "}
        and structured explanations makes your expertise straightforward for retrieval engines to read and extract.
      </>,
  highlight: "Clear, factual content helps conversational engines understand your core capabilities during early buyer research.",
  iconName: "Zap"
}, {
  id: "supplier-product-comparisons",
  title: "Supplier and product comparisons",
  subtitle: "Evaluation & selection",
  description: <>
        When buyers prompt AI systems to compare features, platforms or suppliers, models synthesize answers from across indexed pages and web sources. For online stores and brand catalogues, aligning product data and topical depth through targeted{" "}
        <Link to="/EcommerceSeoPage" className="text-primary font-medium underline underline-offset-2 hover:text-accent transition-colors">
          Ecommerce SEO
        </Link>{" "}
        helps AI engines accurately summarize your specifications, pricing approach and key differences.
      </>,
  highlight: "Structured product information and honest comparison content assist AI tools when summarizing options.",
  iconName: "ShieldCheck"
}, {
  id: "local-professional-shortlists",
  title: "Local and professional shortlists",
  subtitle: "Credible recommendations",
  description: <>
        Decision-makers frequently use conversational queries to shortlist specialist service providers and technical consultancies. Combining verified entity details with targeted{" "}
        <Link to="/B2BSeoLeadGenerationPage" className="text-primary font-medium underline underline-offset-2 hover:text-accent transition-colors">
          B2B SEO &amp; Lead Generation
        </Link>{" "}
        makes it straightforward for AI engines to identify your sector credentials, service scope and geographical coverage when generating recommendations.
      </>,
  highlight: "Consistent business entities and credible service details support discovery during commercial shortlisting.",
  iconName: "Award"
}, {
  id: "beyond-google",
  title: "Beyond Google",
  subtitle: "Cross-platform discovery",
  description: <>
        Search habits are extending across ChatGPT Search, Perplexity and Google AI Overviews alongside regular organic results. Starting with{" "}
        <Link to="/SeoAuditsPage" className="text-primary font-medium underline underline-offset-2 hover:text-accent transition-colors">
          an in-depth SEO audit
        </Link>
        , I assess how your brand is represented across these surfaces to help you earn qualified enquiries rather than chasing traffic volume at any cost.
      </>,
  highlight: "Optimising for clarity across answer surfaces complements your organic rankings and focuses on high-intent enquiries.",
  iconName: "Compass"
}];
export default function Aibenefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const getIcon = (name: string) => {
    switch (name) {
      case "Zap":
        return <Zap className="size-6 text-primary" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-6 text-primary" />;
      case "Award":
        return <Award className="size-6 text-primary" />;
      case "Compass":
        return <Compass className="size-6 text-primary" />;
      default:
        return <Sparkles className="size-6 text-primary" />;
    }
  };
  return <section id="aioptimizationbenefits" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 rounded-md text-xs font-semibold tracking-wide uppercase">
            <Sparkles className="size-3.5 text-accent mr-1 inline-block" />
            Strategic Importance
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-default text-foreground">
            Why AI Search Visibility Matters for Businesses
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Buyers now ask ChatGPT, Google AI Overviews, Perplexity and Bing Copilot to research suppliers, compare options and shortlist services. Being named or referenced in an AI answer shapes buyer consideration before anyone visits a site. As an independent SEO consultant, my goal is helping you earn the right enquiries, measured alongside normal SEO.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {benefits.map((benefit, i) => <motion.div key={benefit.id} data-index={i} variants={itemVariants}>
              <Card className="h-full bg-card text-card-foreground border border-border shadow-md hover:shadow-lg transition-shadow duration-300 rounded-xl p-2 flex flex-col justify-between">
                <div>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 w-fit">
                        {getIcon(benefit.iconName)}
                      </div>
                      <span className="text-xs font-mono text-muted-foreground font-medium">
                        0{i + 1}
                      </span>
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                      <h3 className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                        {benefit.title}
                      </h3>
                    </CardTitle>
                    <p className="text-xs font-medium text-accent uppercase tracking-wider">
                      {benefit.subtitle}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CardDescription className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </CardDescription>
                    <div className="p-3.5 rounded-lg bg-muted/60 border border-border/80 text-xs sm:text-sm text-foreground/90 font-medium">
                      {benefit.highlight}
                    </div>
                  </CardContent>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-16 bg-muted/40 border border-border rounded-xl p-8 md:p-10 text-center flex flex-col sm:flex-row items-center justify-between gap-6 shadow-sm">
          <div className="text-left max-w-2xl space-y-2">
            <h3 className="text-xl sm:text-2xl font-semibold font-default text-foreground">
              Ready to evaluate your AI search visibility?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              Review how your brand is understood by answer engines, strengthen clear entity foundations, or plan a tailored{" "}
              <Link to="/InternationalSeoPage" className="text-primary font-medium underline underline-offset-2 hover:text-accent transition-colors">
                international SEO strategy
              </Link>.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild size="lg" className="rounded-md font-medium text-sm">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                Book a Free SEO Consultation
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}